/**
 * Página pública para a Direção Técnica revisar, editar e aprovar o
 * Plano de Cuidados via token — sem precisar de fazer login.
 *
 * 27/02/2026 — Pedido do utilizador (alcance iii): a DT abre este link
 * a partir do email, vê o plano já pré-preenchido pelo Consultor, ajusta
 * horários/tarefas e aprova, tudo sem sessão.
 *
 * Endpoints usados (todos sem bearer):
 *   GET  /api/public/care-plan-dt/{token}
 *   PUT  /api/public/care-plan-dt/{token}/blocks
 *   POST /api/public/care-plan-dt/{token}/approve
 */
import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Loader2, CheckCircle2, Lock, Save, Plus, Trash2, ClipboardList, Calendar, AlertTriangle, Info } from "lucide-react";
import { toast } from "sonner";
import DepositProgress from "@/components/DepositProgress";

const API = process.env.REACT_APP_BACKEND_URL;

async function apiGet(path) {
  const r = await fetch(`${API}${path}`);
  if (!r.ok) {
    const err = await r.json().catch(() => ({ detail: r.statusText }));
    throw new Error(err.detail || `HTTP ${r.status}`);
  }
  return r.json();
}
async function apiPut(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ detail: r.statusText }));
    throw new Error(err.detail || `HTTP ${r.status}`);
  }
  return r.json();
}
async function apiPost(path, body) {
  const r = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  if (!r.ok) {
    const err = await r.json().catch(() => ({ detail: r.statusText }));
    throw new Error(err.detail || `HTTP ${r.status}`);
  }
  return r.json();
}

// -------- UI helpers --------------------------------------------------

function Section({ title, kicker, icon: Icon, children }) {
  return (
    <section className="bg-white border border-slate-200 rounded-2xl p-6 lg:p-8 mb-6">
      {kicker && (
        <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-2 flex items-center gap-2">
          {Icon && <Icon className="w-3.5 h-3.5" />}
          {kicker}
        </div>
      )}
      <h2 className="text-lg lg:text-xl font-semibold text-slate-900 mb-4">{title}</h2>
      {children}
    </section>
  );
}

function Input({ label, value, onChange, placeholder, disabled, testid, autoValue }) {
  const showAutoPlaceholder = autoValue && !value;
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest">
        {label}
        {autoValue && !value && (
          <span className="ml-2 text-[9px] font-semibold text-amber-600 bg-amber-50 border border-amber-200 rounded-full px-1.5 py-0.5 normal-case tracking-normal">
            auto
          </span>
        )}
      </span>
      <input
        type="text"
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={showAutoPlaceholder ? autoValue : (placeholder || "")}
        disabled={disabled}
        data-testid={testid}
        className={`text-sm bg-white border border-slate-300 rounded-lg px-3 py-2 focus:border-slate-800 focus:outline-none disabled:bg-slate-100 ${showAutoPlaceholder ? "placeholder:text-amber-600 placeholder:italic" : ""}`}
      />
    </label>
  );
}

function Textarea({ label, value, onChange, placeholder, disabled, rows = 3, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[11px] font-semibold text-slate-500 uppercase tracking-widest">{label}</span>
      <textarea
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || ""}
        disabled={disabled}
        rows={rows}
        data-testid={testid}
        className="text-sm bg-white border border-slate-300 rounded-lg px-3 py-2 focus:border-slate-800 focus:outline-none disabled:bg-slate-100 resize-y"
      />
    </label>
  );
}

// -------- Editors -----------------------------------------------------

function ServiceIdentificationEditor({ value, autoValue, onChange, disabled }) {
  const v = value || {};
  const av = autoValue || {};
  const set = (k, x) => onChange({ ...v, [k]: x });
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <Input label="Serviço" value={v.service_type} onChange={(x) => set("service_type", x)}
             autoValue={av.service_type} testid="dt-review-service-type" disabled={disabled} />
      <Input label="Estado" value={v.status} onChange={(x) => set("status", x)}
             placeholder="Ativo" testid="dt-review-status" disabled={disabled} />
      <Input label="Horário" value={v.schedule} onChange={(x) => set("schedule", x)}
             autoValue={av.schedule} testid="dt-review-schedule" disabled={disabled} />
      <Input label="Frequência" value={v.frequency} onChange={(x) => set("frequency", x)}
             autoValue={av.frequency} testid="dt-review-frequency" disabled={disabled} />
      <Input label="Início" value={v.start_date} onChange={(x) => set("start_date", x)}
             autoValue={av.start_date} testid="dt-review-start-date" disabled={disabled} />
      <Input label="Gestor de Cliente" value={v.gestor_cliente_name}
             onChange={(x) => set("gestor_cliente_name", x)}
             autoValue={av.gestor_cliente_name} testid="dt-review-gestor" disabled={disabled} />
    </div>
  );
}

function DailyTasksEditor({ items, onChange, disabled }) {
  const rows = items || [];
  const set = (idx, patch) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const add = () => onChange([...rows, { beneficiary: "both", time: "", what_to_do: "" }]);
  const del = (idx) => onChange(rows.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <div className="text-sm text-slate-500 italic p-3 bg-slate-50 rounded-lg">
          Ainda sem tarefas diárias — clica em "Adicionar tarefa" para começar.
        </div>
      )}
      {rows.map((r, idx) => (
        <div key={idx} className="grid grid-cols-[80px_1fr_auto] gap-3 items-center"
             data-testid={`dt-review-daily-task-${idx}`}>
          <input
            type="text"
            value={r.time || ""}
            onChange={(e) => set(idx, { time: e.target.value })}
            placeholder="HH:MM"
            disabled={disabled}
            className="text-sm bg-white border border-slate-300 rounded-lg px-2.5 py-2 focus:border-slate-800 focus:outline-none disabled:bg-slate-100 font-semibold text-slate-800"
          />
          <input
            type="text"
            value={r.what_to_do || ""}
            onChange={(e) => set(idx, { what_to_do: e.target.value })}
            placeholder="O que fazer nesta hora…"
            disabled={disabled}
            className="text-sm bg-white border border-slate-300 rounded-lg px-3 py-2 focus:border-slate-800 focus:outline-none disabled:bg-slate-100"
          />
          <button
            onClick={() => del(idx)}
            disabled={disabled}
            className="p-2 text-slate-400 hover:text-red-600 disabled:opacity-40"
            title="Remover tarefa"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        disabled={disabled}
        data-testid="dt-review-add-daily-task"
        className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 mt-2 disabled:opacity-40"
      >
        <Plus className="w-3.5 h-3.5" /> Adicionar tarefa
      </button>
    </div>
  );
}

function WeeklyPlanEditor({ items, onChange, disabled }) {
  const rows = items || [];
  const days = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
  const dayLabels = ["S", "T", "Q", "Q", "S", "S", "D"];
  const set = (idx, patch) => {
    const next = [...rows];
    next[idx] = { ...next[idx], ...patch };
    onChange(next);
  };
  const add = () => onChange([...rows, { beneficiary: "both", task: "",
    mon: false, tue: false, wed: false, thu: false, fri: false, sat: false, sun: false }]);
  const del = (idx) => onChange(rows.filter((_, i) => i !== idx));
  return (
    <div className="space-y-2">
      {rows.length === 0 && (
        <div className="text-sm text-slate-500 italic p-3 bg-slate-50 rounded-lg">
          Ainda sem tarefas semanais — clica em "Adicionar" para incluir.
        </div>
      )}
      {rows.length > 0 && (
        <div className="grid grid-cols-[1fr_repeat(7,32px)_auto] gap-2 items-center text-[10px] font-semibold text-slate-500 uppercase tracking-widest px-2">
          <div>Tarefa</div>
          {dayLabels.map((d, i) => <div key={i} className="text-center">{d}</div>)}
          <div className="w-6" />
        </div>
      )}
      {rows.map((r, idx) => (
        <div key={idx} className="grid grid-cols-[1fr_repeat(7,32px)_auto] gap-2 items-center"
             data-testid={`dt-review-weekly-row-${idx}`}>
          <input
            type="text"
            value={r.task || ""}
            onChange={(e) => set(idx, { task: e.target.value })}
            placeholder="Tarefa"
            disabled={disabled}
            className="text-sm bg-white border border-slate-300 rounded-lg px-3 py-2 focus:border-slate-800 focus:outline-none disabled:bg-slate-100"
          />
          {days.map((d) => (
            <label key={d} className="flex items-center justify-center">
              <input
                type="checkbox"
                checked={!!r[d]}
                onChange={(e) => set(idx, { [d]: e.target.checked })}
                disabled={disabled}
                className="w-4 h-4 rounded border-slate-300 accent-slate-800"
              />
            </label>
          ))}
          <button
            onClick={() => del(idx)}
            disabled={disabled}
            className="p-2 text-slate-400 hover:text-red-600 disabled:opacity-40"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      ))}
      <button
        onClick={add}
        disabled={disabled}
        data-testid="dt-review-add-weekly-row"
        className="flex items-center gap-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 mt-2 disabled:opacity-40"
      >
        <Plus className="w-3.5 h-3.5" /> Adicionar tarefa semanal
      </button>
    </div>
  );
}

function StringListEditor({ value, onChange, placeholder, disabled, testid }) {
  // Textarea que trata cada linha como um item da lista
  const text = Array.isArray(value) ? value.join("\n") : (value || "");
  return (
    <Textarea
      value={text}
      onChange={(v) => onChange(v.split("\n").filter((x) => x.trim()))}
      placeholder={placeholder}
      disabled={disabled}
      rows={4}
      testid={testid}
    />
  );
}

// -------- Main page ---------------------------------------------------

export default function PublicCarePlanDTReview() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [dirty, setDirty] = useState({});
  const [saving, setSaving] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveNotes, setApproveNotes] = useState("");

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await apiGet(`/api/public/care-plan-dt/${token}`);
      setData(d);
      setDirty({});
    } catch (e) {
      setError(e.message || "Erro a carregar plano");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [token]);

  const blocks = data?.current_version?.blocks || {};
  const autofill = data?.autofill_preview || {};
  const isFrozen = !!data?.current_version?.is_frozen;

  // Bloco actual (com edições pendentes)
  const effective = useMemo(() => {
    return { ...blocks, ...dirty };
  }, [blocks, dirty]);

  const setBlock = (key, val) => setDirty((d) => ({ ...d, [key]: val }));

  const saveBlock = async (key) => {
    if (saving) return;
    setSaving(key);
    try {
      const patch = { [key]: dirty[key] };
      const r = await apiPut(`/api/public/care-plan-dt/${token}/blocks`, { blocks: patch });
      toast.success("Bloco guardado.");
      setData((prev) => ({
        ...prev,
        current_version: r.current_version,
      }));
      setDirty((d) => {
        const nd = { ...d };
        delete nd[key];
        return nd;
      });
    } catch (e) {
      toast.error(e.message || "Erro a guardar bloco");
    } finally {
      setSaving(null);
    }
  };

  const approve = async () => {
    if (approving) return;
    if (!window.confirm(
      "Confirmar aprovação do Plano de Cuidados?\n\n" +
      "Depois de aprovado, a versão fica DEFINITIVA e não pode ser editada. " +
      "Nova aprovação só é possível após reabertura pela equipa Caring."
    )) return;
    setApproving(true);
    try {
      const r = await apiPost(`/api/public/care-plan-dt/${token}/approve`,
                              { notes: approveNotes });
      toast.success("Plano aprovado!");
      // Refresh page — mostra "definitivo"
      setData((prev) => ({
        ...prev,
        current_version: r.current_version,
      }));
    } catch (e) {
      toast.error(e.message || "Erro a aprovar");
    } finally {
      setApproving(false);
    }
  };

  const SaveBar = ({ blockKey }) => {
    const hasChanges = dirty[blockKey] !== undefined;
    if (!hasChanges || isFrozen) return null;
    return (
      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={() => saveBlock(blockKey)}
          disabled={saving === blockKey || isFrozen}
          data-testid={`dt-review-save-${blockKey}`}
          className="inline-flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40"
        >
          {saving === blockKey ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
          Guardar
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 p-4">
        <div className="max-w-md bg-white border border-red-200 rounded-2xl p-6 shadow-sm">
          <div className="text-[10px] uppercase tracking-widest text-red-600 font-semibold mb-2">
            Link inválido
          </div>
          <h1 className="text-xl font-semibold text-slate-900 mb-2">
            Não conseguimos abrir este pedido
          </h1>
          <p className="text-sm text-slate-600 leading-relaxed">{error}</p>
          <p className="text-xs text-slate-500 mt-4">
            Se o link ficou expirado ou revogado, contacta a equipa Caring para
            receberes um novo.
          </p>
        </div>
      </div>
    );
  }

  const lead = data?.lead || {};

  return (
    <div className="min-h-screen bg-slate-50 pb-16">
      {/* Header */}
      <div className="bg-white border-b border-slate-200">
        <div className="max-w-4xl mx-auto px-6 py-6">
          <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-1">
            Caring · Revisão da Direção Técnica
          </div>
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-slate-900" data-testid="dt-review-heading">
                Plano de Cuidados · {lead.contact_name || "Cliente"}
              </h1>
              <div className="text-sm text-slate-600 mt-1">
                {lead.beneficiary_name && (<>Beneficiário: <b>{lead.beneficiary_name}</b> · </>)}
                Consultor: <b>{lead.consultor_name || "—"}</b>
              </div>
            </div>
            {isFrozen ? (
              <div className="inline-flex items-center gap-2 text-xs font-semibold px-3.5 py-1.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
                   data-testid="dt-review-frozen-badge">
                <Lock className="w-3.5 h-3.5" /> Definitivo
              </div>
            ) : (
              <div className="inline-flex items-center gap-2 text-xs font-semibold px-3.5 py-1.5 rounded-full bg-amber-50 text-amber-700 border border-amber-200">
                <ClipboardList className="w-3.5 h-3.5" /> A aguardar validação
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-6 pt-6">
        {isFrozen && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-emerald-900 text-sm">Plano aprovado</div>
              <div className="text-xs text-emerald-800 mt-1">
                O Plano de Cuidados foi validado pela Direção Técnica e passou a
                <b> definitivo</b>. Os exports em PDF e Word já estão desbloqueados
                para a equipa Caring.
              </div>
            </div>
          </div>
        )}

        {!isFrozen && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 mb-6 flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-700 shrink-0 mt-0.5" />
            <div className="text-xs text-blue-900 leading-relaxed">
              O formulário já está pré-preenchido com toda a informação da
              qualificação, entrevista diagnóstico e reunião técnica. Ajusta o
              horário, as tarefas diárias e o plano semanal do Ajudante Familiar
              e, no fim, aprova o plano.
            </div>
          </div>
        )}

        {/* 1. Identificação do serviço */}
        <Section title="Identificação do serviço" kicker="1 · Panorâmica" icon={ClipboardList}>
          <ServiceIdentificationEditor
            value={effective.service_identification}
            autoValue={autofill.service_identification}
            onChange={(v) => setBlock("service_identification", v)}
            disabled={isFrozen}
          />
          <SaveBar blockKey="service_identification" />
        </Section>

        {/* 2. Tarefas diárias (o coração do plano AF) */}
        <Section title="O que fazer todos os dias" kicker="2 · Tarefas diárias" icon={ClipboardList}>
          <div className="text-sm text-slate-600 mb-4">
            Horário e tarefas que o Ajudante Familiar executa em cada visita.
          </div>
          <DailyTasksEditor
            items={effective.caregiver_daily_tasks}
            onChange={(v) => setBlock("caregiver_daily_tasks", v)}
            disabled={isFrozen}
          />
          <SaveBar blockKey="caregiver_daily_tasks" />
        </Section>

        {/* 3. Plano semanal */}
        <Section title="Plano semanal" kicker="3 · Distribuição por dia" icon={Calendar}>
          <div className="text-sm text-slate-600 mb-4">
            Tarefas que se repetem em dias específicos da semana (ex: banho completo
            só à segunda/quarta/sexta).
          </div>
          <WeeklyPlanEditor
            items={effective.weekly_plan}
            onChange={(v) => setBlock("weekly_plan", v)}
            disabled={isFrozen}
          />
          <SaveBar blockKey="weekly_plan" />
        </Section>

        {/* 4. Antes de terminar */}
        <Section title="Antes de terminar o serviço" kicker="4 · Checklist" icon={CheckCircle2}>
          <div className="text-sm text-slate-600 mb-4">
            Pontos que o Ajudante Familiar deve confirmar antes de sair (um por linha).
          </div>
          <StringListEditor
            value={effective.caregiver_before_ending}
            onChange={(v) => setBlock("caregiver_before_ending", v)}
            placeholder={"Ex:\nVerificar que o utente ficou confortável\nDeixar registo do turno"}
            disabled={isFrozen}
            testid="dt-review-before-ending"
          />
          <SaveBar blockKey="caregiver_before_ending" />
        </Section>

        {/* 5. Quando contactar a Caring */}
        <Section title="Quando contactar a Caring" kicker="5 · Alertas" icon={AlertTriangle}>
          <div className="text-sm text-slate-600 mb-4">
            Situações em que o AF deve informar imediatamente a equipa (um por linha).
          </div>
          <StringListEditor
            value={effective.caregiver_when_to_contact}
            onChange={(v) => setBlock("caregiver_when_to_contact", v)}
            placeholder={"Ex:\nQueda ou trauma físico\nFebre acima de 38ºC"}
            disabled={isFrozen}
            testid="dt-review-when-to-contact"
          />
          <SaveBar blockKey="caregiver_when_to_contact" />
        </Section>

        {/* Blocos apenas informativos (do Consultor · read-only) */}
        {(blocks.objectives?.length > 0 || blocks.priorities?.length > 0 || blocks.recommendations?.length > 0) && (
          <Section title="O que o Consultor preparou" kicker="Consultor · leitura">
            {blocks.objectives?.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2">Objetivos</div>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                  {blocks.objectives.map((o, i) => <li key={i}>{o}</li>)}
                </ul>
              </div>
            )}
            {blocks.priorities?.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2">Prioridades</div>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                  {blocks.priorities.map((p, i) => <li key={i}>{p}</li>)}
                </ul>
              </div>
            )}
            {blocks.recommendations?.length > 0 && (
              <div>
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-widest mb-2">Recomendações</div>
                <ul className="list-disc pl-5 space-y-1 text-sm text-slate-800">
                  {blocks.recommendations.map((r, i) => <li key={i}>{r}</li>)}
                </ul>
              </div>
            )}
          </Section>
        )}

        {/* Approve section */}
        {!isFrozen && (
          <div className="bg-white border-2 border-slate-900 rounded-2xl p-6 lg:p-8">
            <div className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-900 mb-2">
              Aprovação
            </div>
            <h2 className="text-xl font-semibold text-slate-900 mb-4">
              Aprovar como Direção Técnica
            </h2>
            {!lead.proposal_accepted || !lead.deposit_fully_paid ? (
              <div className="rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 mb-4 space-y-3" data-testid="dt-review-approve-gate">
                <div>
                  <div className="text-sm font-semibold text-amber-900 mb-1">
                    Aprovação ainda bloqueada
                  </div>
                  <ul className="text-sm text-amber-800 space-y-0.5 list-disc list-inside">
                    {!lead.proposal_accepted && (
                      <li>Falta a <b>aceitação da proposta</b> pelo cliente.</li>
                    )}
                    {!lead.deposit_fully_paid && (
                      <li>Falta a <b>caução estar totalmente paga</b>.</li>
                    )}
                  </ul>
                  <div className="text-xs text-amber-800/80 mt-2">
                    Podes já rever e ajustar os blocos — assim que estas duas condições estiverem cumpridas, o botão de aprovar fica ativo.
                  </div>
                </div>
                {!lead.deposit_fully_paid && (lead.deposit_plan_total || 0) > 0 && (
                  <DepositProgress
                    paid={lead.deposit_paid_total || 0}
                    total={lead.deposit_plan_total || 0}
                    testid="dt-review-approve-deposit-progress"
                  />
                )}
              </div>
            ) : (
              <p className="text-sm text-slate-700 leading-relaxed mb-4">
                Depois de aprovado, o Plano de Cuidados fica <b>definitivo</b>. Os
                exports em PDF e Word ficam desbloqueados. Guarda quaisquer alterações
                acima <em>antes</em> de aprovar.
              </p>
            )}
            <Textarea
              label="Notas de aprovação (opcional)"
              value={approveNotes}
              onChange={setApproveNotes}
              placeholder="Ex: Aprovado após ajuste do horário nocturno."
              rows={2}
              testid="dt-review-approve-notes"
            />
            <button
              onClick={approve}
              disabled={
                approving ||
                Object.keys(dirty).length > 0 ||
                !lead.proposal_accepted ||
                !lead.deposit_fully_paid
              }
              data-testid="dt-review-approve-btn"
              className="mt-5 inline-flex items-center gap-2 text-sm font-semibold px-5 py-3 rounded-full bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-40 disabled:cursor-not-allowed"
              title={
                !lead.proposal_accepted || !lead.deposit_fully_paid
                  ? "Aguarda aceitação da proposta + caução totalmente paga"
                  : Object.keys(dirty).length > 0
                  ? "Guarda as alterações antes de aprovar"
                  : "Aprovar o Plano de Cuidados"
              }
            >
              {approving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
              {approving ? "A aprovar…" : "Aprovar Plano de Cuidados"}
            </button>
            {Object.keys(dirty).length > 0 && (
              <div className="text-xs text-amber-700 mt-3">
                ⚠ Ainda tens {Object.keys(dirty).length} bloco(s) por guardar. Guarda-os primeiro.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
